import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const CarrierRegistrationPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyCode: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.name === 'companyCode'
      ? e.target.value.toUpperCase()
      : e.target.value;

    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (!formData.companyCode.trim()) {
      setError('Broker code is required');
      setLoading(false);
      return;
    }

    try {
      // 1. Verify broker code exists and check trial status
      const { data: brokerData, error: brokerError } = await supabase
        .from('profiles')
        .select('company_code, subscription_status, trial_ends_at, carrier_limit')
        .eq('company_code', formData.companyCode.trim())
        .eq('role', 'broker')
        .limit(1)
        .single();

      if (brokerError || !brokerData) {
        setError('Invalid broker code. Please check with your broker.');
        setLoading(false);
        return;
      }

      // Check if trial expired
      const isExpired = brokerData.subscription_status === 'expired' ||
        (brokerData.trial_ends_at && new Date(brokerData.trial_ends_at) < new Date());

      if (isExpired) {
        setError('Broker trial has expired. Please ask your broker to upgrade their plan.');
        setLoading(false);
        return;
      }

      // Check carrier limit
      if (brokerData.carrier_limit) {
        const { count } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('company_code', formData.companyCode.trim())
          .eq('role', 'carrier');

        if (count && count >= brokerData.carrier_limit) {
          setError(`Broker has reached carrier limit (${brokerData.carrier_limit}). Please contact your broker.`);
          setLoading(false);
          return;
        }
      }

      // 2. Create auth user with profile metadata
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            role: 'carrier',
            plan: 'Free',
            first_name: formData.firstName,
            last_name: formData.lastName,
            company_code: formData.companyCode.trim(),
            subscription_status: 'active',
          }
        }
      });

      if (signUpError) {
        if (signUpError.message.includes('already registered') ||
            signUpError.message.includes('already been registered') ||
            signUpError.message.includes('User already registered')) {
          setError('This email is already registered. Please login or use a different email.');
        } else {
          setError(signUpError.message);
        }
        setLoading(false);
        return;
      }

      if (!authData.user) {
        setError('Registration failed. Please try again.');
        setLoading(false);
        return;
      }
// Notify broker of new carrier
      await supabase.from('notifications').insert({
        company_code: formData.companyCode.trim(),
        title: 'New Carrier Joined',
        message: `${formData.firstName} ${formData.lastName} joined your network.`,
        type: 'carrier_joined',
      });
      // Success - redirect to login
      navigate('/login', {
        state: { message: 'Registration successful! Please check your email to verify your account.' }
      });

    } catch (err: any) {
      console.error('Registration error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Carrier Registration
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Join a broker on LoadDrive
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="companyCode" className="block text-sm font-medium text-gray-700">
                Broker Code *
              </label>
              <input
                type="text"
                name="companyCode"
                id="companyCode"
                required
                maxLength={10}
                placeholder="e.g., ABC123"
                value={formData.companyCode}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 uppercase"
              />
              <p className="mt-1 text-xs text-gray-500">
                Required - Get this code from your broker
              </p>
            </div>

            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                id="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                id="lastName"
                required
                value={formData.lastName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                id="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <input
                type="password"
                name="password"
                id="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                id="confirmPassword"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Creating Account...' : 'Create Carrier Account'}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <a href="/login" className="text-blue-600 hover:underline font-medium">
                Log in
              </a>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Are you a broker?{' '}
              <a href="/register/broker" className="text-blue-600 hover:underline font-medium">
                Broker registration
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarrierRegistrationPage;